/**
 * cameraRigTemplates — "presets", as graphs.
 *
 * A camera preset used to be an enum whose behaviour was hardcoded in
 * cameraController._applyPreset(): magic numbers (minDistance 1.5, maxPolarAngle 0.52π,
 * pose [0, 1.7+floor, 3.0]) that a blueprint author could neither read nor change. They
 * could pick 'orbit' or 'thirdPerson' and nothing else.
 *
 * Now a preset is a cluster of ordinary nodes. Insert one from the palette and every number
 * is a field on a node you can edit, rewire, or throw away. There is no hidden default
 * underneath — these templates ARE the camera.
 *
 * WHY A JS MODULE, NOT public/blueprints/rigs/*.json: these are EDITOR content, inserted at
 * author time, not runtime content the app fetches. Bundling them keeps the insert
 * synchronous (no async in a context-menu handler), makes them unit-testable directly, and
 * means a core editor action can't fail on a network hiccup. Runtime blueprints under
 * public/blueprints/ are a different thing — those really are fetched.
 *
 * Angles are in DEGREES here because that's what the nodes expose; EventRuntime converts.
 */

/**
 * Free orbit around the avatar — the old setCameraPreset('orbit').
 * Left-drag orbits, middle-drag pans, wheel dollies (matches the shipped
 * conversational.json rig and the editor OrbitControls scheme). Unbounded
 * distance, near-full pitch range: the permissive default.
 *
 * The RIGHT button drives nothing — a right-button drag+release is what browser
 * mouse-gesture software turns into Back/Forward/new-tab, so no camera action
 * hangs off it. Pan is on the middle button; the wheel is the only dolly. See
 * editorCameraControls.js for the same decision on the OrbitControls side.
 */
const ORBIT = {
  label: 'Orbit Camera Rig',
  description: 'Free orbit: left-drag orbits, middle-drag pans, wheel dollies.',
  nodes: [
    { id: 'limits', type: 'cameraSetRigLimits', position: { x: 300, y: 0 },
      data: { minDist: 0.6, maxDist: 0, minPitch: -89, maxPitch: 89 } },

    { id: 'drag_orbit', type: 'onPointerDrag', position: { x: 0, y: 110 },
      data: { button: 'left' } },
    // invert: yaw -= dx, so dragging right swings the scene right (OrbitControls' feel).
    { id: 'yaw', type: 'cameraAddYaw', position: { x: 300, y: 110 },
      data: { delta: 0, sensitivity: 0.005, invert: true } },
    { id: 'pitch', type: 'cameraAddPitch', position: { x: 600, y: 110 },
      data: { delta: 0, sensitivity: 0.005, invert: false } },

    // Middle-drag pans (matching conversational.json's cam_5). Right is left unbound.
    { id: 'drag_pan', type: 'onPointerDrag', position: { x: 0, y: 330 },
      data: { button: 'middle' } },
    { id: 'pan', type: 'cameraPanTarget', position: { x: 300, y: 330 },
      data: { dx: 0, dy: 0, sensitivity: 0.0015 } },

    { id: 'wheel', type: 'onWheel', position: { x: 0, y: 530 }, data: {} },
    { id: 'dolly', type: 'cameraAddDolly', position: { x: 300, y: 530 },
      data: { delta: 0, sensitivity: 0.001, invert: false } },
  ],
  edges: [
    ['drag_orbit', 'execOut', 'yaw', 'execIn'],
    ['drag_orbit', 'out-dx', 'yaw', 'in-delta'],
    ['yaw', 'execOut', 'pitch', 'execIn'],
    ['drag_orbit', 'out-dy', 'pitch', 'in-delta'],
    ['drag_pan', 'execOut', 'pan', 'execIn'],
    ['drag_pan', 'out-dx', 'pan', 'in-dx'],
    ['drag_pan', 'out-dy', 'pan', 'in-dy'],
    ['wheel', 'execOut', 'dolly', 'execIn'],
    ['wheel', 'out-delta', 'dolly', 'in-delta'],
  ],
  beginPlay: ['limits'],
}

/**
 * Over-the-shoulder framing — the old setCameraPreset('thirdPerson'), ported faithfully.
 * Every value below is the one that used to be hardcoded in _applyPreset:
 *
 *   ctl.minDistance = 1.5 / maxDistance = 4.5      → limits.minDist / maxDist
 *   ctl.minPolarAngle = 0.15π (27° from +Y)        → maxPitch  63°  (elevation = 90 - polar)
 *   ctl.maxPolarAngle = 0.52π (93.6° from +Y)      → minPitch  -3.6° ("stay above the floor")
 *   target [0, 1.5, 0], camera [0, 1.7, 3.0]       → pose: dist 3.007, pitch 3.8°, yaw 0
 *   ctl.enablePan = false                          → simply no pan nodes in this rig
 *
 * Note what "pan off" became: not a flag, just the absence of a Camera Pan Target node.
 * That's the whole point — the rig's capabilities are visible in its wiring.
 *
 * The old preset applied its pose relative to stageFloorY (1.5 + floor). The rig doesn't:
 * CameraFloorSync shifts the rig target when the floor moves, so the pose stays floor-relative
 * without every rig having to know about floors.
 */
const THIRD_PERSON = {
  label: 'Third-Person Camera Rig',
  description: 'Over-the-shoulder: tight distance band, no pan, stays above the floor.',
  nodes: [
    { id: 'limits', type: 'cameraSetRigLimits', position: { x: 300, y: 0 },
      data: { minDist: 1.5, maxDist: 4.5, minPitch: -3.6, maxPitch: 63 } },
    { id: 'pose', type: 'cameraSetRigPose', position: { x: 600, y: 0 },
      data: { yaw: 0, pitch: 3.8, dist: 3.007, x: 0, y: 1.5, z: 0, blendMs: 0 } },

    { id: 'drag_orbit', type: 'onPointerDrag', position: { x: 0, y: 200 },
      data: { button: 'left' } },
    { id: 'yaw', type: 'cameraAddYaw', position: { x: 300, y: 200 },
      data: { delta: 0, sensitivity: 0.005, invert: true } },
    { id: 'pitch', type: 'cameraAddPitch', position: { x: 600, y: 200 },
      data: { delta: 0, sensitivity: 0.005, invert: false } },

    { id: 'wheel', type: 'onWheel', position: { x: 0, y: 420 }, data: {} },
    { id: 'dolly', type: 'cameraAddDolly', position: { x: 300, y: 420 },
      data: { delta: 0, sensitivity: 0.001, invert: false } },
  ],
  edges: [
    ['drag_orbit', 'execOut', 'yaw', 'execIn'],
    ['drag_orbit', 'out-dx', 'yaw', 'in-delta'],
    ['yaw', 'execOut', 'pitch', 'execIn'],
    ['drag_orbit', 'out-dy', 'pitch', 'in-delta'],
    ['wheel', 'execOut', 'dolly', 'execIn'],
    ['wheel', 'out-delta', 'dolly', 'in-delta'],
  ],
  beginPlay: ['limits', 'pose'],
}

export const CAMERA_RIG_TEMPLATES = { orbit: ORBIT, thirdPerson: THIRD_PERSON }

/**
 * Instantiate a template into a live graph.
 *
 * Template ids ('yaw', 'limits') are LOCAL — they're remapped to fresh graph ids so the same
 * rig can be inserted twice without colliding. Positions are relative to `origin`.
 *
 * beginPlay wiring: a rig's setup nodes (limits/pose) are spliced into the HEAD of the
 * existing beginPlay chain, so whatever ran first still runs, just after the camera is
 * configured. This matters because the runtime follows ONE execOut edge per node
 * (`edges.find(...)`) — simply adding a second edge off beginPlay would never fire, and the
 * rig would silently do nothing.
 *
 * @param {object}   template   one of CAMERA_RIG_TEMPLATES
 * @param {{x,y}}    origin     where to drop the cluster
 * @param {Function} nextId     () => fresh unique node id
 * @param {object[]} edges      the graph's current edges (read-only)
 * @param {string?}  beginPlayId id of the graph's beginPlay node, if any
 * @returns {{nodes, edges, dropEdgeIds}} nodes/edges to add; dropEdgeIds to remove first
 */
export function instantiateRig(template, origin, nextId, edges = [], beginPlayId = null) {
  const idMap = new Map()
  const nodes = template.nodes.map(n => {
    const id = nextId()
    idMap.set(n.id, id)
    return {
      id,
      type: n.type,
      position: { x: origin.x + n.position.x, y: origin.y + n.position.y },
      data: { ...n.data },
    }
  })

  const map = local => idMap.get(local)
  const newEdges = template.edges.map(([s, sh, t, th], i) => ({
    id: `ee_rig_${map(s)}_${sh}_${i}`,
    type: 'default',
    source: map(s), target: map(t), sourceHandle: sh, targetHandle: th,
  }))

  const dropEdgeIds = []
  const setup = (template.beginPlay ?? []).map(map).filter(Boolean)
  if (beginPlayId && setup.length) {
    // Chain the setup nodes together: begin -> setup[0] -> setup[1] -> ... -> (old first node)
    const existing = edges.find(e => e.source === beginPlayId && (e.sourceHandle === 'execOut' || e.sourceHandle == null))
    newEdges.push({
      id: `ee_rig_begin_${setup[0]}`, type: 'default',
      source: beginPlayId, target: setup[0], sourceHandle: 'execOut', targetHandle: 'execIn',
    })
    for (let i = 0; i < setup.length - 1; i++) {
      newEdges.push({
        id: `ee_rig_chain_${setup[i]}`, type: 'default',
        source: setup[i], target: setup[i + 1], sourceHandle: 'execOut', targetHandle: 'execIn',
      })
    }
    if (existing) {
      newEdges.push({
        id: `ee_rig_tail_${setup[setup.length - 1]}`, type: 'default',
        source: setup[setup.length - 1], target: existing.target,
        sourceHandle: 'execOut', targetHandle: 'execIn',
      })
      dropEdgeIds.push(existing.id)
    }
  }

  return { nodes, edges: newEdges, dropEdgeIds }
}

/**
 * Instantiate a template as a single, self-contained `graphGroup` node (camera-in-blueprint
 * Phase 3) — the group-era replacement for the palette's old "splice into the caller's
 * beginPlay chain" insertion. A `graphGroup` carries no exec/data pins in v1, so there is no
 * outside beginPlay chain to splice setup nodes (limits/pose) into — but the group's OWN inner
 * graph can carry its own beginPlay, and the runtime dispatches it: `fireEvent('beginPlay')`
 * walks every group's starters via `collectGroupStarters` (EventRuntime.js), so an inner
 * beginPlay fires against the group's own pool exactly like the top-level one does.
 *
 * So this mints one fresh onConstruct node local to the group and feeds it to `instantiateRig`
 * as the starter id, reusing the same tested splice path `instantiateRig` already has (see
 * cameraRigTemplates.test.mjs): the input-driven half (drag/wheel → yaw/pitch/pan/dolly) is
 * wired exactly as before, and the setup nodes (cameraSetRigLimits / cameraSetRigPose) are
 * chained onConstruct → limits → pose so they actually apply on drop — for `thirdPerson` this
 * is what makes the tuned distance band / pitch range / initial pose take effect instead of
 * silently falling back to the controller's built-in defaults.
 *
 * Wave 2B: the starter is `onConstruct`, not the retired `beginPlay` — the merged node fires
 * once the avatar loads (construct gate) and again on every editor edit, so the rig limits/pose
 * live-tune through the wire. Old saved groups with an inner beginPlay are retyped at load by
 * blueprintMigrations.migrateEventNodes.
 */
export function instantiateRigAsGroup(template, position, nextId) {
  const starter = { id: nextId(), type: 'onConstruct', position: { x: 0, y: -140 }, data: {} }
  const { nodes: innerNodes, edges: innerEdges } =
    instantiateRig(template, { x: 0, y: 0 }, nextId, [], starter.id)
  return {
    id: nextId(),
    type: 'graphGroup',
    position,
    data: { name: template.label, innerNodes: [starter, ...innerNodes], innerEdges },
  }
}
